'use client';

import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useEffect, useState, useMemo } from 'react';
import { useTheme } from 'next-themes';
import { calculatePMC, DailyTSS } from '@/lib/pmc';
import { ParsedFitResult } from '@/lib/fitParser';
import { ActivityMetric } from '@/lib/pbSync';
import { Upload, Map as MapIcon, Route, Mountain, Clock, Trash2 } from 'lucide-react';
import { MapPanel } from '@/components/TrailRunMapView';
import localforage from 'localforage';

// ─── Shared tokens ──────────────────────────────────────────────────────────
const C = {
  blue:    '#3b82f6',
  teal:    '#14b8a6',
  emerald: '#10b981',
  rose:    '#f43f5e',
  amber:   '#f59e0b',
  slate:   '#94a3b8',
};

function base(isDark: boolean) {
  return {
    text: isDark ? '#94a3b8' : '#6b7280',
    grid: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    tt:   { backgroundColor: isDark ? '#1c1c1e' : '#fff', borderColor: 'transparent', textStyle: { color: isDark ? '#f0f0f0' : '#1a1a1a', fontSize: 11 } },
  };
}

function StatCard({ label, value, accent, sub }: { label: string; value: string | number; accent?: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:gap-1 p-3 sm:p-5 rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)]">
      <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <span className="text-xl sm:text-3xl font-semibold tracking-tight" style={{ color: accent }}>{value}</span>
      {sub && <span className="text-[10px] sm:text-xs text-muted-foreground">{sub}</span>}
    </div>
  );
}

function ChartCard({ title, desc, children, span = '' }: { title: string; desc?: string; children: React.ReactNode; span?: string }) {
  return (
    <div className={`flex flex-col rounded-2xl bg-card border border-border/50 shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)] overflow-hidden ${span}`}>
      <div className="px-5 pt-5 pb-4 border-b border-border/40">
        <p className="text-sm font-semibold tracking-tight">{title}</p>
        {desc && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>}
      </div>
      <div className="p-5 flex-1">{children}</div>
    </div>
  );
}

function formatTime(sec: number) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

// ─── Component ──────────────────────────────────────────────────────────────
export function PredictiveView({
  realData,
  metricsData,
}: {
  realData?: ParsedFitResult[] | null;
  metricsData?: ActivityMetric[];
}) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [gpxRoute, setGpxRoute] = useState<{ lats: number[], lngs: number[], alts: number[], dist: number, gain: number, name: string } | null>(null);

  useEffect(() => { 
    setMounted(true);
    localforage.getItem('savedGpxRoute').then((val) => {
      if (val) setGpxRoute(val as any);
    }).catch(console.error);
  }, []);

  const isDark = mounted ? resolvedTheme === 'dark' : true;
  const { text, grid, tt } = base(isDark);

  const handleGPXUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const xmlText = ev.target?.result as string;
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlText, "application/xml");
      const trkpts = Array.from(doc.querySelectorAll("trkpt"));
      
      let totalDist = 0;
      let totalGain = 0;
      const lats: number[] = [];
      const lngs: number[] = [];
      const alts: number[] = [];
      
      for (let i = 0; i < trkpts.length; i++) {
        const pt = trkpts[i];
        const lat = parseFloat(pt.getAttribute("lat") || "0");
        const lon = parseFloat(pt.getAttribute("lon") || "0");
        const eleNode = pt.querySelector("ele");
        const alt = eleNode ? parseFloat(eleNode.textContent || "0") : 0;
        
        lats.push(lat);
        lngs.push(lon);
        alts.push(alt);
        
        if (i > 0) {
          const prevPt = trkpts[i-1];
          const prevLat = parseFloat(prevPt.getAttribute("lat") || "0");
          const prevLon = parseFloat(prevPt.getAttribute("lon") || "0");
          const prevEle = prevPt.querySelector("ele");
          const prevAlt = prevEle ? parseFloat(prevEle.textContent || "0") : 0;
          
          // Haversine distance
          const R = 6371e3;
          const r1 = lat * Math.PI/180;
          const r2 = prevLat * Math.PI/180;
          const d1 = (prevLat-lat) * Math.PI/180;
          const d2 = (prevLon-lon) * Math.PI/180;
          const a = Math.sin(d1/2)*Math.sin(d1/2) + Math.cos(r1)*Math.cos(r2)*Math.sin(d2/2)*Math.sin(d2/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          totalDist += R * c;
          
          if (alt > prevAlt) {
            totalGain += (alt - prevAlt);
          }
        }
      }
      const newRoute = { lats, lngs, alts, dist: totalDist, gain: totalGain, name: file.name };
      setGpxRoute(newRoute);
      localforage.setItem('savedGpxRoute', newRoute).catch(console.error);
    };
    reader.readAsText(file);
  };

  const computed = useMemo(() => {
    // 1. Calculate future PMC based on the average daily TSS of the last 14 days
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tssMap = new Map<string, number>();
    let last14DaysTSS = 0;

    if (realData) {
      realData.forEach(w => {
        const key = w.startTime.toISOString().split('T')[0];
        let tss = 0;
        if (w.normalizedPower) tss = (w.totalDuration / 3600) * ((w.normalizedPower / 184) ** 2) * 100;
        else if (w.avgHeartRate > 100) tss = (w.totalDuration / 3600) * ((w.avgHeartRate - 100) / 100) * 100;
        else tss = (w.totalDuration / 3600) * 50;
        tssMap.set(key, (tssMap.get(key) || 0) + tss);

        if (w.startTime.getTime() >= today.getTime() - 14 * 86400000 && w.startTime.getTime() <= today.getTime()) {
          last14DaysTSS += tss;
        }
      });
    }

    const avgDailyTss = Math.max(last14DaysTSS / 14, 20); // Cap floor to avoid flatlining completely

    // Build timeline: 60 days past -> 30 days future
    let spanStart = new Date(today);
    spanStart.setDate(today.getDate() - 60);

    const pastDates: string[] = [];
    for (const d = new Date(spanStart); d <= today; d.setDate(d.getDate() + 1)) {
      pastDates.push(d.toISOString().split('T')[0]);
    }

    const futureDates: string[] = [];
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const futureEnd = new Date(today);
    futureEnd.setDate(futureEnd.getDate() + 30); // 30-day projection

    for (const d = new Date(tomorrow); d <= futureEnd; d.setDate(d.getDate() + 1)) {
      futureDates.push(d.toISOString().split('T')[0]);
      // Project the average daily TSS forward
      tssMap.set(d.toISOString().split('T')[0], avgDailyTss);
    }

    const allDates = [...pastDates, ...futureDates];
    const pmcInput: DailyTSS[] = allDates.map(d => ({ date: d, tss: tssMap.get(d) || 0 }));
    const pmc = calculatePMC(pmcInput, 38, 28);

    const projectedCTL = pmc[pmc.length - 1]?.ctl || 0;
    const projectedTSB = pmc[pmc.length - 1]?.tsb || 0;
    const currentCTL = pmc[pastDates.length - 1]?.ctl || 0;

    // 2. Predict Race Pacing (30 Days Out)
    const latestVo2 = metricsData?.filter(m => m.vo2max != null).pop()?.vo2max || 40;
    
    // Calculate a realistic 30-day VO2max improvement based on current sustained training volume
    // E.g., An average of 50 TSS/day over 30 days yields a +0.66 VO2max increase.
    const projectedVo2 = latestVo2 + (avgDailyTss / 75);
    
    // Using a hybrid-athlete tuned formula perfectly locked to recent PBs (28:16, 1:05:39, 2:08:12):
    // Pace (min/km) ≈ 288 / VO2max. 
    // 5k time (sec) = (288 / VO2max) * 60 * 5 = 86400 / VO2max
    const p5k = 86400 / projectedVo2;
    // Fatigue scaling precisely matched to the user's known endurance curve
    const p10k = p5k * 2.32;
    const p21k = p5k * 4.54;

    // Trail scaling (Moderate elevation + technical terrain)
    const t5k = p5k * 1.15;
    const t10k = p10k * 1.18;
    const t21k = p21k * 1.22;

    return {
      allDates, pmc, pastEndIdx: pastDates.length - 1,
      projectedCTL, projectedTSB, currentCTL,
      latestVo2, projectedVo2, p5k, p10k, p21k, t5k, t10k, t21k, avgDailyTss
    };
  }, [realData, metricsData]);

  if (!mounted) return null;

  const axisX = (data: string[]) => ({
    type: 'category' as const, data, boundaryGap: false,
    axisLine: { lineStyle: { color: grid } }, axisTick: { show: false },
    axisLabel: { color: text, fontSize: 10, interval: 6, formatter: (val: string) => new Date(val).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) },
  });

  const axisY = (name = '') => ({
    type: 'value' as const, name,
    axisLine: { show: false }, axisTick: { show: false },
    axisLabel: { color: text, fontSize: 10 },
    splitLine: { lineStyle: { color: grid, type: 'dashed' as const } },
  });

  const pmcOpt = {
    backgroundColor: 'transparent', textStyle: { color: text, fontFamily: 'Inter, sans-serif', fontSize: 11 },
    tooltip: { trigger: 'axis', ...tt },
    legend: { data: ['Fitness', 'Fatigue', 'Form'], textStyle: { color: text, fontSize: 11 }, bottom: 0, itemWidth: 12, itemHeight: 12 },
    grid: { left: 0, right: 0, bottom: 32, top: 24, containLabel: true },
    xAxis: axisX(computed.allDates),
    yAxis: axisY(),
    series: [
      { name: 'Fitness',    type: 'line', data: computed.pmc.map(d => d.ctl), showSymbol: false, smooth: 0.3, lineStyle: { color: C.blue,  width: 2.5 }, itemStyle: { color: C.blue  } },
      { name: 'Fatigue',    type: 'line', data: computed.pmc.map(d => d.atl), showSymbol: false, smooth: 0.3, lineStyle: { color: C.rose,  width: 2 }, itemStyle: { color: C.rose  } },
      { name: 'Form',       type: 'line', data: computed.pmc.map(d => d.tsb), showSymbol: false, smooth: 0.3, lineStyle: { color: C.teal,  width: 2, type: 'dashed' as const }, itemStyle: { color: C.teal } },
      {
        name: 'Projection Start',
        type: 'line',
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: text, type: 'dashed' as const, width: 1.5 },
          data: [{ xAxis: computed.pastEndIdx, label: { formatter: 'Today', color: text, position: 'insideEndTop' } }]
        }
      }
    ],
  };

  return (
    <div className="space-y-10">
      
      <div>
        <h2 className="text-base font-semibold tracking-tight">30-Day Training Projection</h2>
        <p className="text-sm text-muted-foreground mt-0.5 mb-4">Assuming you maintain your recent 14-day average of {Math.round(computed.avgDailyTss)} TSS/day.</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <StatCard label="Current CTL"   value={Math.round(computed.currentCTL)}   accent={C.blue} />
          <StatCard label="Projected CTL" value={Math.round(computed.projectedCTL)} accent={computed.projectedCTL > computed.currentCTL ? C.emerald : C.amber} sub="In 30 days" />
          <StatCard label="Projected TSB" value={Math.round(computed.projectedTSB)} accent={C.teal} sub="Form in 30 days" />
        </div>

        <ChartCard title="Future Performance Management" desc="Displays your past 60 days of training alongside a 30-day forward projection based on your current volume.">
          <ReactECharts option={pmcOpt} style={{ height: 340 }} />
        </ChartCard>
      </div>

      <div>
        <h2 className="text-base font-semibold tracking-tight">Predicted Race Pacing (In 30 Days)</h2>
        <p className="text-sm text-muted-foreground mt-0.5 mb-4">
          Based on your current training load, your VO₂max is projected to increase from {Math.round(computed.latestVo2 * 10) / 10} to <span className="font-medium text-foreground">{Math.round(computed.projectedVo2 * 10) / 10}</span>.
        </p>
        
        <h3 className="text-sm font-semibold tracking-tight mt-6 mb-3 text-muted-foreground uppercase">Road (Flat / Fast)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="5K Race Time"  value={formatTime(computed.p5k)} accent={C.emerald} sub={`${Math.floor((computed.p5k / 5) / 60)}:${Math.floor((computed.p5k / 5) % 60).toString().padStart(2, '0')} min/km pace`} />
          <StatCard label="10K Race Time" value={formatTime(computed.p10k)} accent={C.teal} sub={`${Math.floor((computed.p10k / 10) / 60)}:${Math.floor((computed.p10k / 10) % 60).toString().padStart(2, '0')} min/km pace`} />
          <StatCard label="Half Marathon" value={formatTime(computed.p21k)} accent={C.blue} sub={`${Math.floor((computed.p21k / 21.1) / 60)}:${Math.floor((computed.p21k / 21.1) % 60).toString().padStart(2, '0')} min/km pace`} />
        </div>

        <h3 className="text-sm font-semibold tracking-tight mt-8 mb-3 text-muted-foreground uppercase">Trail (Moderate Tech & Elevation)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Trail 5K"  value={formatTime(computed.t5k)} accent={C.amber} sub={`${Math.floor((computed.t5k / 5) / 60)}:${Math.floor((computed.t5k / 5) % 60).toString().padStart(2, '0')} min/km pace`} />
          <StatCard label="Trail 10K" value={formatTime(computed.t10k)} accent={C.rose} sub={`${Math.floor((computed.t10k / 10) / 60)}:${Math.floor((computed.t10k / 10) % 60).toString().padStart(2, '0')} min/km pace`} />
          <StatCard label="Trail Half" value={formatTime(computed.t21k)} accent={C.slate} sub={`${Math.floor((computed.t21k / 21.1) / 60)}:${Math.floor((computed.t21k / 21.1) % 60).toString().padStart(2, '0')} min/km pace`} />
        </div>
      </div>

      {/* GPX Upload & Custom Prediction */}
      <div className="pt-8 border-t border-border/40">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Custom Course Predictor</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Upload a GPX course to estimate your finish time.</p>
          </div>
          <div className="flex items-center gap-2">
            {gpxRoute && (
              <button
                onClick={() => { setGpxRoute(null); localforage.removeItem('savedGpxRoute'); }}
                className="flex items-center gap-1.5 px-3 py-2 text-rose-500 hover:bg-rose-500/10 text-xs font-semibold rounded-lg transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
            <label className="flex items-center gap-2 px-4 py-2 bg-foreground text-background text-sm font-medium rounded-xl cursor-pointer hover:opacity-90 transition-opacity">
              <Upload className="w-4 h-4" />
              Upload GPX
              <input type="file" accept=".gpx" className="hidden" onChange={handleGPXUpload} />
            </label>
          </div>
        </div>

        {gpxRoute && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col p-6 bg-card border border-border/50 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)]">
                <h3 className="font-semibold tracking-tight mb-6">{gpxRoute.name}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-sky-500/10 flex items-center justify-center">
                      <Route className="w-4 h-4 text-sky-500" />
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Distance</p>
                      <p className="font-semibold">{(gpxRoute.dist / 1000).toFixed(2)} km</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <Mountain className="w-4 h-4 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Elevation</p>
                      <p className="font-semibold">+{Math.round(gpxRoute.gain)} m</p>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center gap-4 bg-emerald-500/10 p-5 rounded-2xl mt-4 border border-emerald-500/20">
                    <Clock className="w-8 h-8 text-emerald-500" />
                    <div>
                      <p className="text-[10px] text-emerald-600/80 dark:text-emerald-400/80 uppercase font-semibold tracking-wide">Estimated Finish Time</p>
                      <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">
                        {(() => {
                          const dist = gpxRoute.dist;
                          const gain = gpxRoute.gain;
                          
                          // 1. Endurance Scaling (Pete Riegel Formula)
                          // We scale the fatigue exponent based on the user's Chronic Training Load (avgDailyTss).
                          // High volume = lower decay (1.04). Low volume = higher decay (1.09).
                          let exponent = 1.06;
                          if (computed.avgDailyTss > 85) exponent = 1.04;
                          else if (computed.avgDailyTss < 35) exponent = 1.09;
                          else exponent = 1.09 - ((computed.avgDailyTss - 35) / 50) * 0.05;

                          const baseSeconds = computed.p5k * Math.pow(dist / 5000, exponent);

                          // 2. Elevation Penalty (Tobler's Hiking Function / Naismith Variant)
                          // Fitter athletes (higher VO2max) suffer slightly less pace decay on climbs.
                          // For running/trail, a 400m climb over a distance usually adds about 10-15 minutes.
                          // Therefore, the penalty is roughly 1.5 to 3 seconds per meter of climb.
                          const climbingPenaltyPerMeter = Math.max(1.5, 3.5 - (computed.projectedVo2 / 20));
                          
                          // 3. Technical Terrain Multiplier
                          // Extremely steep routes (>40m gain per km) involve technical walking/scrambling which adds a non-linear time penalty.
                          const gainPerKm = gain / (dist / 1000 || 1);
                          const technicalMultiplier = gainPerKm > 40 ? 1 + ((gainPerKm - 40) * 0.0025) : 1;

                          const totalSeconds = (baseSeconds + (gain * climbingPenaltyPerMeter)) * technicalMultiplier;
                          const avgPaceSecs = totalSeconds / (dist / 1000 || 1);
                          
                          return (
                            <div className="flex items-baseline gap-3">
                              {formatTime(totalSeconds)}
                              <span className="text-sm font-medium opacity-80">
                                ({Math.floor(avgPaceSecs / 60)}:{Math.floor(avgPaceSecs % 60).toString().padStart(2, '0')} /km)
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="h-[300px] lg:h-full min-h-[300px] rounded-2xl border border-border/50 bg-card overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.28)] relative z-0">
              <MapPanel lats={gpxRoute.lats} lngs={gpxRoute.lngs} alts={gpxRoute.alts} hoverIdx={null} isDark={isDark} />
            </div>

            {/* Algorithm Explanation */}
            <div className="col-span-1 lg:col-span-2 p-5 bg-muted/30 border border-border/40 rounded-2xl text-xs text-muted-foreground mt-2 space-y-2">
              <p className="font-semibold text-foreground">How is this calculated?</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Base Pace:</strong> Derived from your 30-day projected VO₂max ({Math.round(computed.projectedVo2 * 10) / 10}).</li>
                <li><strong>Endurance Decay:</strong> Uses Riegel's scaling formula. The fatigue exponent is dynamically tuned by your Chronic Training Load (CTL). High volume = less pace decay over long distances.</li>
                <li><strong>Elevation Penalty:</strong> A modified Tobler/Naismith function. Uphills add a specific time penalty per meter gained, which is actively reduced based on your aerobic fitness level.</li>
                <li><strong>Technical Terrain:</strong> If the route is extremely steep (&gt;40m gain per km), a non-linear multiplier is applied to account for walking/scrambling speeds.</li>
              </ul>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
